import useAsyncFn from 'react-use/lib/useAsyncFn';

import type { SignupAttribution } from './attribution';

export type { SignupAttribution } from './attribution';

export enum Region {
  RegionEu = 'REGION_EU',
  RegionUs = 'REGION_US',
}
export type AuthConnection = 'googleAuthConnection' | 'passwordAuthConnection';

type AuthConnectionConfig = Record<string, unknown>;

export enum BillingDetailsTaxType {
  EuVat = 'EU_VAT',
  GbVat = 'GB_VAT',
  UsEin = 'US_EIN',
  NoVat = 'NO_VAT',
  AdNrt = 'AD_NRT',
  IsVat = 'IS_VAT',
  RsPib = 'RS_PIB',
  ChVat = 'CH_VAT',
  TrTin = 'TR_TIN',
  UaVat = 'UA_VAT',
  CaBn = 'CA_BN',
  MxRfc = 'MX_RFC',
  IlVat = 'IL_VAT',
  ArCuit = 'AR_CUIT',
  BoTin = 'BO_TIN',
  ClTin = 'CL_TIN',
  CoNit = 'CO_NIT',
  UyRuc = 'UY_RUC',
  VeRif = 'VE_RIF',
  IdNpwp = 'ID_NPWP',
  MyFrp = 'MY_FRP',
  PhTin = 'PH_TIN',
  SgGst = 'SG_GST',
  ThVat = 'TH_VAT',
  VnTin = 'VN_TIN',
  KrBrn = 'KR_BRN',
  TwVat = 'TW_VAT',
  JpTrn = 'JP_TRN',
}

export type BillingDetails = {
  displayName: string;
  email: string;
};

export type CreateAccountRequest = {
  displayName: string;
  loginId: string;
  allowedLoginEmailDomains?: string[];
  adminEmail?: string;
  region: Region;
  authConnections: Record<AuthConnection, AuthConnectionConfig>[];
  billingDetails?: BillingDetails;
  marketingOptIn?: boolean;
  userRole?: string;
  userGoals?: string[];
  signupAttribution?: SignupAttribution;
};

export function useCreateAccount(inviteId?: string) {
  return useAsyncFn(
    async (params: CreateAccountRequest, accessToken?: string) => {
      const {
        billingDetails,
        marketingOptIn,
        userRole,
        userGoals,
        signupAttribution,
        ...account
      } = params;
      if (!accessToken) {
        throw new Error(
          'You need to supply an access token to create an account.',
        );
      }
      let url = 'https://onboarding.confidence.dev/v1/accounts';
      if (inviteId) {
        url += `?invite=accountInvites%2f${inviteId}`;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          account,
          billingDetails,
          marketingOptIn,
          userRole,
          userGoals,
          ...(signupAttribution
            ? { signup_attribution: signupAttribution }
            : undefined),
        }),
      });
      const json = await response.json();
      if (!response.ok) {
        throw new Error(json.message);
      }
      return json;
    },
    [],
  );
}
